import {
  Button, Card, Spinner, Text, Title3, makeStyles, mergeClasses, shorthands, tokens,
  // Divider, // used by the temporarily hidden "mutual federation" setting below

} from '@fluentui/react-components';
import { ArrowLeft24Regular, PeopleTeam24Regular } from '@fluentui/react-icons';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmationDialog } from '../components/Common/Dialogs';
import { SettingToggle } from '../components/Common/SettingToggle';
import { SettingsBanner } from '../components/Common/SettingsBanner';
import { useSavingGuard } from '../hooks/useSavingGuard';
import { useScrollableMessage } from '../hooks/useScrollableMessage';
import { TenantConfig, tenantConfigService } from '../services/tenantConfigService';
import { useCommonSettingsStyles } from './commonSettingsStyles';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', ...shorthands.gap('24px') },
  card: { ...shorthands.padding('24px'), display: 'flex', flexDirection: 'column', ...shorthands.gap('24px') },
  sectionTitle: { color: tokens.colorBrandForeground1, fontSize: '20px' },
  header: { marginBottom: 0 },
  desc: { fontSize: '14px', color: '#000000' },
});

type ToggleKey = keyof TenantConfig;

interface DialogCopy { title: string; message: string; }

const ON_DIALOGS: Record<ToggleKey, DialogCopy> = {
  enableExternalAccessRestrictionsForChatParticipants: {
    title: 'Apply external access restrictions?',
    message:
      "This change applies to all users in your tenant. Users who don't meet the new requirements will be removed from affected group chats and will lose access to those chat histories. Do you want to continue?",
  },
  enableMutualFederationForChatParticipants: {
    title: 'Apply mutual federation requirements?',
    message:
      'This change applies to all users in your tenant. Users without the required mutual federation relationships will be removed from existing group chats and will lose access to those chat histories. Do you want to continue?',
  },
};

export const TenantConfiguration: React.FC = () => {
  const styles = useStyles();
  const common = useCommonSettingsStyles();
  const navigate = useNavigate();

  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingKey, setPendingKey] = useState<ToggleKey | null>(null);
  const [savingKey, setSavingKey] = useState<ToggleKey | null>(null);
  const [success, setSuccess] = useScrollableMessage<string | null>(null);
  const [error, setError] = useScrollableMessage<string | null>(null);

  const saving = savingKey !== null;
  const { requestNavigation } = useSavingGuard(saving);

  useEffect(() => {
    tenantConfigService
      .getTenantConfig()
      .then(setConfig)
      .catch(() => setError('There was a problem. Try again later.'))
      .finally(() => setLoading(false));
  }, [setError]);

  const persist = async (key: ToggleKey, value: boolean) => {
    if (!config) return;
    setSavingKey(key);
    try {
      await tenantConfigService.updateTenantConfig({ [key]: value });
      setConfig((prev) => (prev ? { ...prev, [key]: value } : prev));
      setSuccess('Settings updated');
      setError(null);
    } catch {
      setError('There was a problem. Try again later.');
      setSuccess(null);
    } finally {
      setSavingKey(null);
    }
  };

  const requestEnable = (key: ToggleKey) => setPendingKey(key);
  const disableSetting = (key: ToggleKey) => void persist(key, false);

  const confirmOn = async () => {
    const key = pendingKey;
    setPendingKey(null);
    if (key) await persist(key, true);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner size="large" label="Loading settings..." data-testid="tenant-config-loading-spinner" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className={styles.page} data-testid="tenant-config-page">
        <div className={mergeClasses(common.header, styles.header)}>
          <Text className={common.title}><PeopleTeam24Regular /> Tenant Configuration</Text>
          <Button appearance="secondary" icon={<ArrowLeft24Regular />}
            onClick={() => navigate('/settings')} data-testid="tenant-config-back-button">
            Back to Settings
          </Button>
        </div>
        {error && (
          <SettingsBanner intent="error" message={error} onDismiss={() => setError(null)}
            barTestId="tenant-config-error-bar" dismissTestId="tenant-config-error-dismiss" />
        )}
      </div>
    );
  }

  const renderToggle = (
    key: ToggleKey, label: string, desc: string, testid: string,
  ) => (
    <SettingToggle
      label={label}
      description={desc}
      checked={config[key]}
      saving={savingKey === key}
      disabled={savingKey !== null}
      onChange={(checked) => (checked ? requestEnable(key) : disableSetting(key))}
      switchTestId={testid}
      savingTestId={`${testid}-saving`}
    />
  );

  return (
    <div className={styles.page} data-testid="tenant-config-page">
      <div className={mergeClasses(common.header, styles.header)}>
        <Text className={common.title}><PeopleTeam24Regular /> Tenant Configuration</Text>
        <Button appearance="secondary" icon={<ArrowLeft24Regular />}
          onClick={() => requestNavigation(() => navigate('/settings'))} data-testid="tenant-config-back-button">
          Back to Settings
        </Button>
      </div>

      {error && (
        <SettingsBanner intent="error" message={error} onDismiss={() => setError(null)}
          barTestId="tenant-config-error-bar" dismissTestId="tenant-config-error-dismiss" />
      )}
      {success && (
        <SettingsBanner intent="success" message={success} onDismiss={() => setSuccess(null)}
          barTestId="tenant-config-success-bar" dismissTestId="tenant-config-success-dismiss" />
      )}

      <Card className={styles.card}>
        <Title3 className={styles.sectionTitle}>Federated group chat restrictions</Title3>
        <Text className={styles.desc}>
          Control how users in your organisation can participate in federated group chats. These
          settings apply to all users across your tenant.
        </Text>
        {renderToggle(
          'enableExternalAccessRestrictionsForChatParticipants',
          'Enable external access restrictions for chat participants',
          'Blocks users without federation access from joining group chats that include external members. These users are also removed from existing group chats with external members.',
          'tenant-config-external-access-switch',
        )}
        {/* Temporarily hidden: "Enable mutual federation for chat participants" setting.
        <Divider data-testid="tenant-config-settings-divider" />
        {renderToggle(
          'enableMutualFederationForChatParticipants',
          'Enable mutual federation for chat participants',
          'Requires all group chat participants to have mutual federation relationships with each other. Users without valid relationships are blocked from joining and removed from existing chats.',
          'tenant-config-mutual-federation-switch',
        )}
        */}
      </Card>

      <ConfirmationDialog
        open={pendingKey !== null}
        onClose={() => setPendingKey(null)}
        onConfirm={confirmOn}
        title={pendingKey ? ON_DIALOGS[pendingKey].title : ''}
        message={pendingKey ? ON_DIALOGS[pendingKey].message : ''}
      />
    </div>
  );
};
